/**
 * Empire-PWA chassis · on-device LLM via transformers.js.
 *
 * Vision: Xenova/clip-vit-base-patch16 (WebGPU, ~150 MB INT8, ~3 s warm load)
 * Text:   onnx-community/Phi-3-mini-128k-instruct-onnx-web (deferred to v1.1)
 *
 * The loader is lazy and cached in the SW for offline second-run. Calls are
 * coalesced — if two components request CLIP, only one model load happens.
 */

interface ImageEmbedder {
  embedImage(blob: Blob): Promise<Float32Array>;
  embedText(text: string): Promise<Float32Array>;
}

let clipPromise: Promise<ImageEmbedder> | null = null;

interface TransformersFns {
  pipeline: (task: string, model: string, opts?: Record<string, unknown>) => Promise<unknown>;
  AutoProcessor: { from_pretrained(model: string): Promise<unknown> };
  AutoTokenizer: { from_pretrained(model: string): Promise<unknown> };
  CLIPVisionModelWithProjection?: { from_pretrained(model: string, opts?: Record<string, unknown>): Promise<unknown> };
  CLIPTextModelWithProjection?: { from_pretrained(model: string, opts?: Record<string, unknown>): Promise<unknown> };
  RawImage: { fromBlob(b: Blob): Promise<unknown> };
}

async function loadTransformers(): Promise<TransformersFns> {
  // @ts-expect-error -- CDN-loaded module; types are introduced lazily at runtime
  const mod = (await import('https://cdn.jsdelivr.net/npm/@huggingface/transformers@3.1.2/dist/transformers.min.js')) as TransformersFns;
  return mod;
}

export async function getClip(): Promise<ImageEmbedder> {
  if (clipPromise) return clipPromise;
  clipPromise = (async () => {
    const T = await loadTransformers();
    const modelName = 'Xenova/clip-vit-base-patch16';
    const dtype = 'q8';
    const device = (await canUseWebGPU()) ? 'webgpu' : 'wasm';

    const processor = await T.AutoProcessor.from_pretrained(modelName) as { (input: unknown): Promise<{ pixel_values: unknown }> };
    const tokenizer = await T.AutoTokenizer.from_pretrained(modelName) as { (text: string, opts?: Record<string, unknown>): unknown };
    if (!T.CLIPVisionModelWithProjection || !T.CLIPTextModelWithProjection) {
      throw new Error('CLIP projection models missing in transformers.js export');
    }
    const visionModel = await T.CLIPVisionModelWithProjection.from_pretrained(modelName, { dtype, device }) as { (input: unknown): Promise<{ image_embeds: { data: Float32Array; ort_tensor?: { data: Float32Array } } }> };
    const textModel = await T.CLIPTextModelWithProjection.from_pretrained(modelName, { dtype, device }) as { (input: unknown): Promise<{ text_embeds: { data: Float32Array } }> };

    return {
      async embedImage(blob: Blob): Promise<Float32Array> {
        const img = await T.RawImage.fromBlob(blob);
        const inputs = await processor(img);
        const out = await visionModel(inputs);
        const data = out.image_embeds.data ?? out.image_embeds.ort_tensor?.data;
        if (!data) throw new Error('image embedding missing');
        return l2norm(new Float32Array(data));
      },
      async embedText(text: string): Promise<Float32Array> {
        const inputs = tokenizer(text, { padding: true, truncation: true });
        const out = await textModel(inputs);
        return l2norm(new Float32Array(out.text_embeds.data));
      },
    };
  })();
  return clipPromise;
}

async function canUseWebGPU(): Promise<boolean> {
  const nav = navigator as Navigator & { gpu?: { requestAdapter(): Promise<unknown> } };
  if (!nav.gpu) return false;
  try {
    const adapter = await nav.gpu.requestAdapter();
    return adapter !== null;
  } catch {
    return false;
  }
}

function l2norm(v: Float32Array): Float32Array {
  let s = 0;
  for (const x of v) s += x * x;
  const m = Math.sqrt(s) || 1;
  const out = new Float32Array(v.length);
  for (let i = 0; i < v.length; i++) out[i] = v[i]! / m;
  return out;
}

export function cosine(a: Float32Array, b: Float32Array): number {
  let s = 0;
  const n = Math.min(a.length, b.length);
  for (let i = 0; i < n; i++) s += a[i]! * b[i]!;
  return s;
}

export interface ClipReady {
  readonly available: true;
  readonly device: 'webgpu' | 'wasm';
}
export interface ClipUnavailable {
  readonly available: false;
  readonly reason: string;
}

export async function probeClip(): Promise<ClipReady | ClipUnavailable> {
  const webgpu = await canUseWebGPU();
  return { available: true, device: webgpu ? 'webgpu' : 'wasm' };
}
